import { useEffect, useState } from "react";
import { Instagram, Loader2, Plus, Trash2, Eye, EyeOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/api";

const AdminInstagram = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ permalink: "", caption: "", is_active: true });

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/instagram-posts");
      setPosts(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Erro ao carregar publicações.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.permalink.trim()) {
      toast.error("Cole a URL da publicação do Instagram.");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post("/admin/instagram-posts", form);
      toast.success("Publicação adicionada. Já aparece no app.");
      setForm({ permalink: "", caption: "", is_active: true });
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao adicionar.");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (post) => {
    try {
      await adminApi.put(`/admin/instagram-posts/${post.id}`, { is_active: !post.is_active });
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const remove = async (post) => {
    if (!window.confirm("Remover esta publicação do app? A publicação continua no Instagram.")) return;
    try {
      await adminApi.delete(`/admin/instagram-posts/${post.id}`);
      toast.success("Publicação removida.");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-instagram">
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold mb-1">
          Painel · Instagram
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase text-[var(--fi-blue)] leading-tight">
          Publicações no app
        </h1>
        <p className="text-sm text-gray-600 mt-2 max-w-2xl">
          Cole a URL de qualquer post, reel ou IGTV do <b>@falaitapira</b> e ele aparece direto na seção Instagram do app,
          com o embed oficial da Meta. Você controla o que fica em destaque, sem precisar de token ou conta profissional.
        </p>
      </div>

      {/* Add form */}
      <form onSubmit={submit} className="bg-white rounded-2xl p-5 shadow-[0_6px_24px_rgba(13,27,75,0.06)] space-y-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-[var(--fi-blue)]">
            URL da publicação *
          </label>
          <input
            data-testid="admin-ig-permalink"
            value={form.permalink}
            onChange={(e) => setForm({ ...form, permalink: e.target.value })}
            placeholder="https://www.instagram.com/p/ABC123..."
            className="input mt-1"
          />
          <div className="text-xs text-gray-500 mt-1">
            No Instagram, abra o post → toque nos três pontinhos → <b>Copiar link</b>. Cole aqui.
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-[var(--fi-blue)]">
            Legenda curta (opcional — só para você organizar)
          </label>
          <input
            data-testid="admin-ig-caption"
            value={form.caption}
            onChange={(e) => setForm({ ...form, caption: e.target.value })}
            placeholder="Ex: Reportagem sobre feira do produtor"
            className="input mt-1"
          />
        </div>
        <button
          type="submit"
          data-testid="admin-ig-add-btn"
          disabled={busy}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[var(--fi-red)] hover:bg-[var(--fi-red-hover)] text-white font-bold uppercase tracking-widest text-xs transition-colors disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Adicionar publicação
        </button>
      </form>

      {/* List */}
      <div>
        <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-3">
          {posts.length} publicaç{posts.length !== 1 ? "ões" : "ão"} cadastrada{posts.length !== 1 ? "s" : ""}
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-10 text-gray-400 border-2 border-dashed border-gray-200 rounded-2xl">
            <Instagram className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Nenhuma publicação ainda. Adicione a primeira acima.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="admin-ig-list">
            {posts.map((p) => (
              <li
                key={p.id}
                data-testid={`admin-ig-item-${p.id}`}
                className={`flex items-center gap-3 p-3 bg-white rounded-xl border ${p.is_active ? "border-gray-200" : "border-gray-100 opacity-60"}`}
              >
                <Instagram className="w-6 h-6 text-[var(--fi-blue)] shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm text-[var(--fi-blue)] truncate">
                    {p.caption || p.permalink.replace("https://www.instagram.com", "")}
                  </div>
                  <a
                    href={p.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-500 hover:text-[var(--fi-red)] inline-flex items-center gap-1 truncate"
                  >
                    {p.permalink} <ExternalLink className="w-3 h-3 shrink-0" />
                  </a>
                </div>
                <button
                  onClick={() => toggle(p)}
                  data-testid={`admin-ig-toggle-${p.id}`}
                  className="w-9 h-9 rounded-lg border border-gray-200 hover:border-[var(--fi-blue)] flex items-center justify-center transition-colors"
                  title={p.is_active ? "Ocultar do app" : "Exibir no app"}
                  aria-label={p.is_active ? "Ocultar" : "Exibir"}
                >
                  {p.is_active ? <Eye className="w-4 h-4 text-[var(--fi-blue)]" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                </button>
                <button
                  onClick={() => remove(p)}
                  data-testid={`admin-ig-delete-${p.id}`}
                  className="w-9 h-9 rounded-lg border border-gray-200 hover:border-[var(--fi-red)] hover:bg-red-50 flex items-center justify-center transition-colors"
                  title="Remover"
                  aria-label="Remover"
                >
                  <Trash2 className="w-4 h-4 text-[var(--fi-red)]" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminInstagram;
